import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import * as THREE from "three";
import {
  disposeScene,
  type ThreeDriver,
  type ThreeHostContext,
} from "../dui/three-host/dui-three-host.ts";
import "../dui/three-host/dui-three-host.ts";

// ============================================
// COLOR CONFIGURATION - Modify these variables
// ============================================
// Base colors (OKLCH reference values in comments)
// Background: oklch(0.25 0.017 243) - app background
// Brand:      oklch(0.728 0.1304 73.28) - brand accent (wave crests only)

const CONFIG = {
  // Blue-dominant gradient (all in hue 243°, varying L and C)
  colorBackground: "#1b2329", // oklch(0.25 0.017 243)
  colorDark: "#252a36", // oklch(0.30 0.025 243)
  colorMid: "#3b4559", // oklch(0.40 0.040 243)
  colorLight: "#526178", // oklch(0.50 0.055 243)
  colorBright: "#6b7f99", // oklch(0.60 0.070 243)
  // Brand color - only at wave crests
  colorHighlight: "#d4a24e", // oklch(0.728 0.1304 73.28)

  // Animation speed
  morphSpeed: 0.3,
  shiftSpeed: 0.15,

  // ASCII character size (pixels)
  charSize: 10,

  // Noise scale (lower = larger patterns)
  noiseScale: 0.025,

  // Where brand color begins (0.0-1.0, higher = only the tallest peaks)
  crestThreshold: 0.7,
};

// ASCII characters from dark to light
const ASCII_CHARS = " .:-=+*#%@";

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;
  
  uniform float uTime;
  uniform vec2 uResolution;
  uniform float uCharSize;
  uniform float uNoiseScale;
  uniform float uMorphSpeed;
  uniform float uShiftSpeed;
  uniform float uCrestThreshold;
  uniform sampler2D uAsciiTexture;
  uniform vec3 uColorBg;
  uniform vec3 uColorDark;
  uniform vec3 uColorMid;
  uniform vec3 uColorLight;
  uniform vec3 uColorBright;
  uniform vec3 uColorHighlight;
  
  varying vec2 vUv;
  
  // Simplex 3D noise
  vec4 permute(vec4 x) { return mod(((x*34.0)+1.0)*x, 289.0); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  
  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    
    i = mod(i, 289.0);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
      
    float n_ = 1.0/7.0;
    vec3 ns = n_ * D.wyz - D.xzx;
    
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    
    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
  
  // Fractal Brownian Motion
  float fbm(vec3 p) {
    float value = 0.0;
    float amplitude = 0.5;
    float frequency = 1.0;
    for (int i = 0; i < 4; i++) {
      value += amplitude * snoise(p * frequency);
      amplitude *= 0.5;
      frequency *= 2.0;
    }
    return value;
  }
  
  // Color gradient - blue body with brand crests at peaks
  vec3 getColor(float v, float crestThreshold) {
    v = clamp(v, 0.0, 1.0);
    
    // Below crest threshold: blue gradient
    if (v < crestThreshold) {
      // Remap 0-threshold to full blue gradient
      float t = v / crestThreshold;
      if (t < 0.25) return mix(uColorBg, uColorDark, t * 4.0);
      if (t < 0.5) return mix(uColorDark, uColorMid, (t - 0.25) * 4.0);
      if (t < 0.75) return mix(uColorMid, uColorLight, (t - 0.5) * 4.0);
      return mix(uColorLight, uColorBright, (t - 0.75) * 4.0);
    }
    
    // Above threshold: transition from bright blue to brand (the crest)
    float crestT = (v - crestThreshold) / (1.0 - crestThreshold);
    return mix(uColorBright, uColorHighlight, crestT);
  }
  
  void main() {
    // Calculate grid cell
    vec2 cellSize = vec2(uCharSize) / uResolution;
    vec2 cell = floor(vUv / cellSize);
    vec2 cellCenter = (cell + 0.5) * cellSize;
    vec2 cellUv = fract(vUv / cellSize);
    
    // Calculate noise at cell center
    vec2 noisePos = cellCenter * uResolution * uNoiseScale;
    float morphTime = uTime * uMorphSpeed;
    float shiftTime = uTime * uShiftSpeed;
    
    // Layer multiple noise functions for the main pattern
    float n1 = fbm(vec3(noisePos, morphTime));
    float n2 = fbm(vec3(noisePos + 100.0, morphTime * 0.7 + 50.0));
    float n3 = snoise(vec3(noisePos * 0.5 + shiftTime, morphTime * 0.3));
    
    // Combine noises for base pattern
    float noise = (n1 + n2 * 0.5 + n3 * 0.3) / 1.8;
    noise = noise * 0.5 + 0.5; // Normalize to 0-1
    
    // Add wave distortion
    float wave = sin(cellCenter.x * 10.0 + uTime * 0.5) * 0.1;
    wave += cos(cellCenter.y * 8.0 + uTime * 0.3) * 0.1;
    noise = clamp(noise + wave, 0.0, 1.0);
    
    // Map to ASCII character index
    float charIndex = floor(noise * 9.99);
    
    // Sample ASCII texture
    float charWidth = 1.0 / 10.0; // 10 characters
    vec2 asciiUv = vec2(
      (charIndex + cellUv.x) * charWidth,
      1.0 - cellUv.y
    );
    float ascii = texture2D(uAsciiTexture, asciiUv).r;
    
    // Get color - brand only emerges at wave crests
    vec3 color = getColor(noise, uCrestThreshold);
    
    // Apply ASCII mask
    vec3 bgColor = uColorBg;
    vec3 finalColor = mix(bgColor, color, ascii);
    
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

/** Convert hex color to THREE.Vector3 (RGB 0-1) */
function hexToVec3(hex: string): THREE.Vector3 {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return new THREE.Vector3(r, g, b);
}

/** Create ASCII character texture atlas */
function createAsciiTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  const charWidth = 8;
  const charHeight = 12;
  canvas.width = charWidth * ASCII_CHARS.length;
  canvas.height = charHeight;

  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#fff";
  ctx.font = "12px monospace";
  ctx.textBaseline = "top";

  for (let i = 0; i < ASCII_CHARS.length; i++) {
    ctx.fillText(ASCII_CHARS[i], i * charWidth + 1, 0);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  return texture;
}

/** ASCII morphing shader driver */
const asciiMorphDriver = (ctx: ThreeHostContext): ThreeDriver => {
  const scene = new THREE.Scene();

  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const asciiTexture = createAsciiTexture();

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(ctx.width, ctx.height) },
      uCharSize: { value: CONFIG.charSize },
      uNoiseScale: { value: CONFIG.noiseScale },
      uMorphSpeed: { value: CONFIG.morphSpeed },
      uShiftSpeed: { value: CONFIG.shiftSpeed },
      uCrestThreshold: { value: CONFIG.crestThreshold },
      uAsciiTexture: { value: asciiTexture },
      uColorBg: { value: hexToVec3(CONFIG.colorBackground) },
      uColorDark: { value: hexToVec3(CONFIG.colorDark) },
      uColorMid: { value: hexToVec3(CONFIG.colorMid) },
      uColorLight: { value: hexToVec3(CONFIG.colorLight) },
      uColorBright: { value: hexToVec3(CONFIG.colorBright) },
      uColorHighlight: { value: hexToVec3(CONFIG.colorHighlight) },
    },
  });

  const geometry = new THREE.PlaneGeometry(2, 2);
  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  ctx.invalidate();

  return {
    scene,
    camera,
    render: (_delta, elapsed) => {
      material.uniforms.uTime.value = elapsed;
    },
    resize: (width, height) => {
      material.uniforms.uResolution.value.set(width, height);
    },
    dispose: () => {
      asciiTexture.dispose();
      material.dispose();
      geometry.dispose();
      disposeScene(scene);
    },
  };
};

const styles = css`
  :host {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

@customElement("home-shader")
export class HomeShaderElement extends LitElement {
  static override styles = [styles];

  override render(): TemplateResult {
    return html`
      <dui-three-host
        .driver="${asciiMorphDriver}"
        renderMode="continuous"
        backgroundColor="${CONFIG.colorBackground}"
      ></dui-three-host>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "home-shader": HomeShaderElement;
  }
}
